import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
} from '@mui/material';
import { useWarehouses } from '../../../lib/query/hooks';
import { downloadCSV } from '../../../lib/utils';
import type { SimpleAccount } from '@/lib/types';

interface WarehouseListProps {
  account: SimpleAccount;
}

export function WarehouseList({ account }: WarehouseListProps) {
  const { data: warehouses = [], isLoading: loading, error } = useWarehouses(account.code);

  // CSVダウンロード処理
  const handleDownloadCSV = () => {
    const csvData = warehouses.map((warehouse) => ({
      倉庫カスタマーID: warehouse.customer_id,
      倉庫名: warehouse.name,
    }));

    const filename = `倉庫一覧_${account.code}_${new Date().toISOString().split('T')[0]}.csv`;
    downloadCSV(csvData, filename);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>倉庫一覧を読み込み中...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error instanceof Error ? error.message : '倉庫一覧の読み込みに失敗しました。'}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Card>
        <CardContent>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
          >
            <Typography variant="h6">倉庫一覧 ({warehouses.length}件)</Typography>
            {warehouses.length > 0 && (
              <Button variant="outlined" onClick={handleDownloadCSV} size="small">
                CSVでダウンロード
              </Button>
            )}
          </Box>

          {warehouses.length === 0 ? (
            <Alert severity="info">倉庫データが見つかりませんでした。</Alert>
          ) : (
            <TableContainer
              component={Paper}
              sx={{
                mt: 2,
                overflowX: 'auto',
                maxWidth: '100%',
                '& .MuiTable-root': {
                  minWidth: 650,
                },
              }}
            >
              <Table sx={{ whiteSpace: 'nowrap' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>倉庫カスタマーID</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>倉庫名</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>物流倉庫フラグ</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {warehouses.map((warehouse, index) => (
                    <TableRow key={`${warehouse.id}-${index}`}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{warehouse.customer_id}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{warehouse.name}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Chip
                          label={warehouse.logistics_flag ? '物流倉庫' : '店舗倉庫'}
                          color={warehouse.logistics_flag ? 'primary' : 'default'}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
